import type {
  CargoTemplateInDTO,
  DistributionTemplateInDTO,
  EditDataLocationAndOrderOutDTO,
  EditLocationAndOrderInDTO,
  EditTemplateLocationOrderInDTO,
} from '@api/ringil-api.v2.tsx';
import { DistributionTypeEnum } from '@api/ringil-api.v2.tsx';
import type { TemplateEditHandlerView } from '@app/pages/customer/TemplateEdit/types.ts';
import { isNullOrUndefined } from '@utils/utils.tsx';

/**
 * RD-3062 — the form ↔ API mapping of the expedition template editor (`TemplateEditHandler`), moved out
 * of the provider unchanged so it can be unit-tested. The provider keeps the state and API calls.
 */

/** Body of `cuUpdateEditTemplateLocationAndOrder`: one stop of the template with its orders. */
export const templateLocationBody = (data: EditDataLocationAndOrderOutDTO): EditLocationAndOrderInDTO => ({
  locationName: data.locationName,
  fromAddressBookId: data.fromAddressBookId,
  city: data.city,
  contactEmail: data.contactEmail,
  country: data.country,
  orders: data.orders.map(o => {
    return {
      id: o.id,
      orderName: o.orderName,
      items: o.items.map(c => {
        return {
          cargoItemTypeCode: c.cargoTemplate?.code,
          amount: c.amount,
          weight: c.weight,
          length: c.length,
          width: c.width,
          weightMode: c.weightMode,
          height: c.height,
          quantityUnit: c.quantityUnit,
          stackable: c.stackable,
          itemNote: c.itemNote,
        } as CargoTemplateInDTO;
      }),
    } as EditTemplateLocationOrderInDTO;
  }),
  pickupCode: data.pickupCode,
  contactName: data.contactName,
  contactPhone: data.contactPhone,
  streetNr: data.streetNr,
  zipCode: data.zipCode,
  timezoneId: data.timezoneId,
  openingHourDefinitions: data.openingHourDefinitions,
  /* timeslots: data.timeslots.map((i) => {
    return {
      till: i.till,
      since: i.since,
      dayOfWeek: i.dayOfWeek,
    };
  }),*/
});

/**
 * Switches the template's carrier distribution on or off (an immer draft is changed in place). The first
 * time it is switched on it starts as a published spot distribution every 96 hours in the default currency.
 */
export const toggleCreateDistribution = (draft: TemplateEditHandlerView, defaultCurrency: string): void => {
  if (isNullOrUndefined(draft.distribution))
    draft.distribution = {
      enabled: false,
      currency: defaultCurrency,
      isPublished: true,
    };
  draft.distribution.enabled = !draft.distribution.enabled;
  if (draft.distribution.enabled) {
    if (isNullOrUndefined(draft.distribution.intervalHour)) {
      draft.distribution.intervalHour = 96;
    }
    if (isNullOrUndefined(draft.distribution.selectedItems)) draft.distribution.selectedItems = [];
    if (isNullOrUndefined(draft.distribution.distributionType)) {
      draft.distribution.distributionType = DistributionTypeEnum.SPOT;
    }
  }
};

/** Body of `cuCreateDistributionTemplate`, built from the editor state. */
export const createDistributionTemplateBody = (state: TemplateEditHandlerView): DistributionTemplateInDTO => ({
  price: state.distribution.price,
  intervalHour: state.distribution.intervalHour,
  name: state.templateName,
  currency: state.distribution.currency,
  distributionType: state.distribution.distributionType,
  templateExpeditionId: state.templateExpeditionId,
  carriersAndGroups: state.distribution.selectedItems,
  shouldExpeditionAutomaticallyPublished: state.distribution.isPublished,
});
